import {useCallback, useEffect, useRef} from 'react';

import {logger} from 'utils';

const LOG_PREFIX = '[Speech Detection]';
const withLogPrefix = (message: string) => `${LOG_PREFIX} ${message}`;
// Loud enough to be speech rather than room tone. Measured as RMS over the raw samples, so it is independent of
// the device's gain settings in a way a peak threshold is not. Detection now serves barge-in only (recording is
// continuous), so the bias is the other way from before: a cough or a door must not cut the bot off, so this
// wants real speech-level energy, not the quietest possible onset.
const SPEECH_RMS_THRESHOLD = 0.02;
// How long the client has to stay quiet before we call the barge-in "over". Nothing depends on the exact value
// anymore (recording does not stop with the phrase), so it only debounces the onSpeechEnd signal.
const SILENCE_HANGOVER = 900;
// Ignore a single loud sample (a click, a door) — speech has to hold up for at least this long before we treat
// it as the client barging in and silence the bot.
const SPEECH_ONSET = 40;
// ~43 ms of audio per callback at 48 kHz: fast enough that barge-in silences the bot the moment the client
// starts talking. The node only measures — it neither records nor forwards the samples.
const ANALYSER_BUFFER_SIZE = 2048;

type Props = {
    track: MediaStreamTrack;
    enabled: boolean;
    onSpeechStart: () => void;
    onSpeechEnd: () => void;
};

// Barge-in for hands-free voice input: the recording runs continuously and the transcription provider draws the
// phrase boundaries, so this does not gate recording at all. Its only job is to notice the client starting to
// talk over the bot so the caller can silence the playback — talking over a reply means they want to be heard now.
//
// Deliberately energy-based rather than a model: it runs on every browser with no download, and the cost of a
// mistake is small (a slightly late or early silence of the bot). The microphone is captured with echo
// cancellation, which is what keeps the agent's own voice from tripping it.
//
// Driven by the audio graph rather than requestAnimationFrame: a hidden tab stops painting frames, and a
// rAF-driven detector would simply stop noticing the client there.
export function useSpeechDetection({track, enabled, onSpeechStart, onSpeechEnd}: Props) {
    const contextRef = useRef<AudioContext>(null);
    const processorRef = useRef<ScriptProcessorNode>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode>(null);
    const speakingRef = useRef(false);
    const loudSinceRef = useRef<number>(null);
    const quietSinceRef = useRef<number>(null);
    // Callbacks change identity on every render of the caller; a ref keeps the audio graph from being rebuilt.
    const callbacksRef = useRef({onSpeechStart, onSpeechEnd});
    callbacksRef.current = {onSpeechStart, onSpeechEnd};

    const stop = useCallback(() => {
        processorRef.current?.disconnect();
        processorRef.current = null;
        sourceRef.current?.disconnect();
        sourceRef.current = null;
        if (contextRef.current) {
            void contextRef.current.close().catch(() => null);
            contextRef.current = null;
        }
        if (speakingRef.current) {
            speakingRef.current = false;
            callbacksRef.current.onSpeechEnd();
        }
        loudSinceRef.current = null;
        quietSinceRef.current = null;
    }, []);

    useEffect(() => {
        if (!enabled || !track || track.readyState !== 'live') {
            stop();
            return;
        }

        try {
            const context = new (window.AudioContext || (window as any).webkitAudioContext)();
            // Safari (and Chrome without a prior gesture on this context) hands back a suspended context, whose
            // callbacks never fire — the microphone would sit lit while nothing was ever detected.
            if (context.state === 'suspended') {
                void context.resume().catch((error) => logger.error(withLogPrefix('Unable to resume the audio context'), error));
            }

            const source = context.createMediaStreamSource(new MediaStream([track]));
            const processor = context.createScriptProcessor(ANALYSER_BUFFER_SIZE, 1, 1);
            contextRef.current = context;
            sourceRef.current = source;
            processorRef.current = processor;

            processor.onaudioprocess = (event) => {
                const samples = event.inputBuffer.getChannelData(0);
                let sum = 0;
                for (let i = 0; i < samples.length; i++) {
                    sum += samples[i] * samples[i];
                }
                const rms = Math.sqrt(sum / samples.length);
                const now = Date.now();

                if (rms >= SPEECH_RMS_THRESHOLD) {
                    quietSinceRef.current = null;
                    loudSinceRef.current = loudSinceRef.current || now;
                    if (!speakingRef.current && now - loudSinceRef.current >= SPEECH_ONSET) {
                        speakingRef.current = true;
                        callbacksRef.current.onSpeechStart();
                    }
                } else {
                    loudSinceRef.current = null;
                    quietSinceRef.current = quietSinceRef.current || now;
                    if (speakingRef.current && now - quietSinceRef.current >= SILENCE_HANGOVER) {
                        speakingRef.current = false;
                        callbacksRef.current.onSpeechEnd();
                    }
                }
            };

            source.connect(processor);
            // A ScriptProcessor only runs while it is connected to a destination. Nothing of ours reaches the
            // speakers: the node writes no output, so the destination receives silence.
            processor.connect(context.destination);
        } catch (error) {
            logger.error(withLogPrefix('Unable to listen for speech'), error);
        }

        return stop;
    }, [enabled, track, stop]);
}
